import React from 'react';
import { SectionCard } from './SectionCard';
import { PanelStateMessage } from './PanelStateMessage';
import { ChartViewSelector } from './chartView/ChartViewSelector';
import { useChartView } from './chartView/useChartView';
import {
  DefaultRankingBarList,
  RankingBarChart,
  RankingPieChart,
  buildRankingChartData,
} from './chartView/RankingCharts';
import { formatMoney, formatNumber } from '../../lib/formatMoney';
import { useTheme } from '../../context/ThemeContext';

const PANEL_TITLE = 'Revenue by Product';
const PANEL_DESCRIPTION = 'Top products by sales contribution';

function EmptyState() {
  return (
    <div className="py-6 text-center text-sm text-slate-400">
      No product data found for this period
    </div>
  );
}

function SkeletonRow() {
  return (
    <div className="grid animate-pulse gap-2 sm:grid-cols-[minmax(0,1fr)_2fr_auto] sm:items-center">
      <div className="h-3 w-24 rounded bg-slate-200" />
      <div className="h-2.5 rounded bg-slate-200" />
      <div className="h-3 w-16 rounded bg-slate-200" />
    </div>
  );
}

function SkeletonTable() {
  return (
    <div className="space-y-3 animate-pulse">
      {[...Array(6)].map((_, index) => (
        <div key={index} className="grid grid-cols-[minmax(0,1.4fr)_auto_auto] gap-3">
          <div className="h-3 rounded bg-slate-200" />
          <div className="h-3 w-10 rounded bg-slate-100" />
          <div className="h-3 w-16 rounded bg-slate-200" />
        </div>
      ))}
    </div>
  );
}

function DefaultCompactView({ chartData, currencyCode }) {
  return (
    <DefaultRankingBarList
      data={chartData}
      formatValue={(value) => formatMoney(value, { currency: currencyCode })}
    />
  );
}

function DefaultDetailedView({ products, currencyCode }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[320px] text-[12px]">
        <thead>
          <tr className="border-b border-slate-200 text-left text-[10px] uppercase tracking-[0.08em] text-slate-500">
            <th className="pb-2 pr-3 font-semibold">Product</th>
            <th className="pb-2 px-3 text-right font-semibold">Qty</th>
            <th className="pb-2 pl-3 text-right font-semibold">Revenue</th>
          </tr>
        </thead>
        <tbody>
          {products.map((product) => (
            <tr key={product.productName} className="border-b border-slate-100 last:border-b-0">
              <td className="py-2.5 pr-3 font-medium text-slate-700">{product.productName}</td>
              <td className="py-2.5 px-3 text-right text-slate-500">{formatNumber(product.quantity)}</td>
              <td className="py-2.5 pl-3 text-right font-semibold text-[#201C35]">
                {formatMoney(product.totalRevenue, { currency: currencyCode })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div id="product-revenue-announcer" className="sr-only" aria-live="polite" />
    </div>
  );
}

function ProductRevenueContent({ products, chartView, isCompact, currencyCode }) {
  const { chartPalette } = useTheme();
  const chartData = buildRankingChartData(products, 'productName', 'totalRevenue', chartPalette);

  if (chartView === 'bar') {
    return (
      <RankingBarChart
        data={chartData}
        currencyCode={currencyCode}
        compact={isCompact}
        leadingTitle="Leading product"
        leadingCaption="Primary revenue driver"
        ariaLabel="Bar chart of revenue by product"
      />
    );
  }

  if (chartView === 'pie') {
    return (
      <RankingPieChart
        data={chartData}
        currencyCode={currencyCode}
        compact={isCompact}
        leadingTitle="Leading product"
        leadingCaption="Primary revenue driver"
        ariaLabel="Pie chart of revenue by product"
      />
    );
  }

  if (isCompact) {
    return <DefaultCompactView chartData={chartData} currencyCode={currencyCode} />;
  }

  return <DefaultDetailedView products={products} currencyCode={currencyCode} />;
}

export function ProductRevenuePanel({ revenueByProduct, loading, error, variant = 'detailed', currencyCode = 'DKK' }) {
  const isCompact = variant === 'compact';
  const productLimit = isCompact ? 3 : 10;
  const [chartView, setChartView] = useChartView('panelChartView.productRevenue', 'bar');
  const headerAction = <ChartViewSelector value={chartView} onChange={setChartView} disabled={loading} />;

  if (error) {
    return (
      <SectionCard title={PANEL_TITLE} description={PANEL_DESCRIPTION} headerAction={headerAction}>
        <PanelStateMessage
          variant="error"
          title="Unable to load product revenue"
          message={error?.message || 'Try adjusting the filters or retrying the request.'}
        />
      </SectionCard>
    );
  }

  if (loading) {
    return (
      <SectionCard title={PANEL_TITLE} description={PANEL_DESCRIPTION} headerAction={headerAction}>
        {isCompact ? (
          <div className="space-y-2.5">
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </div>
        ) : (
          <SkeletonTable />
        )}
      </SectionCard>
    );
  }

  const products = (revenueByProduct || []).slice(0, productLimit);

  if (products.length === 0) {
    return (
      <SectionCard title={PANEL_TITLE} description={PANEL_DESCRIPTION} headerAction={headerAction}>
        <EmptyState />
      </SectionCard>
    );
  }

  return (
    <SectionCard title={PANEL_TITLE} description={PANEL_DESCRIPTION} headerAction={headerAction}>
      <ProductRevenueContent
        products={products}
        chartView={chartView}
        isCompact={isCompact}
        currencyCode={currencyCode}
      />
    </SectionCard>
  );
}
